import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HELICOPTER_PACKAGES } from "@/types/helicopter";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface HelicopterBookingFormProps {
  selectedPackageId?: string;
}

const HelicopterBookingForm = ({ selectedPackageId }: HelicopterBookingFormProps) => {
  const [packageId, setPackageId] = useState<string>(selectedPackageId || "");
  const [guests, setGuests] = useState<number>(1);
  const [date, setDate] = useState<Date>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const selectedPackage = HELICOPTER_PACKAGES.find(p => p.id === packageId);
  
  // Calculate total price based on guests
  const calculateTotalPrice = () => {
    if (!selectedPackage) return 0;
    // Grand Explorer is private only - fixed price
    if (selectedPackage.isPrivateOnly) {
      return selectedPackage.price;
    }
    // For other packages, price per guest
    return selectedPackage.price * guests;
  };

  const totalPrice = calculateTotalPrice();

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setIsCalendarOpen(false);
  };

  const handleBookNow = async () => {
    if (!packageId || !guests || !date) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-strabl-payment', {
        body: {
          amount: totalPrice,
          packageId: packageId,
          packageName: selectedPackage?.name || '',
          guests: guests,
          date: date.toISOString(),
          bookingType: 'helicopter'
        }
      });

      if (error) {
        toast.error("Failed to process booking. Please try again.");
        setIsProcessing(false);
        return;
      }

      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.manualCheckout) {
        toast.info("Redirecting to payment...");
        await createCartInBrowser();
      } else {
        toast.error("Failed to get checkout URL");
        setIsProcessing(false);
      }
    } catch {
      toast.error("An error occurred. Please try again.");
      setIsProcessing(false);
    }
  };

  const createCartInBrowser = async () => {
    const apiUrl = 'https://api.strabl.com';
    const platformUuid = import.meta.env.VITE_STRABL_PLATFORM_UUID;
    
    if (!platformUuid) {
      toast.error("Payment configuration error");
      setIsProcessing(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/v1/carts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform_uuid: platformUuid,
          items: [{
            name: `Helicopter Ride - ${selectedPackage?.name}`,
            quantity: guests,
            unit_amount: Math.round((totalPrice / guests) * 100),
          }],
          success_url: `${window.location.origin}/payment-success`,
          failure_url: `${window.location.origin}/payment-failure`,
        }),
      });

      const result = await response.json();
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        toast.error("Failed to create payment session");
        setIsProcessing(false);
      }
    } catch {
      toast.error("Payment error. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-background/95 backdrop-blur-sm rounded-lg p-6 shadow-xl border">
      <h2 className="text-xl font-bold text-primary mb-1">Book Your Helicopter Ride</h2>
      <p className="text-sm text-muted-foreground mb-6">Experience Dubai from above</p>

      <div className="space-y-4">
        {/* Package Select */}
        <div>
          <label className="block text-sm font-medium mb-2">Select Package</label>
          <Select value={packageId} onValueChange={setPackageId}>
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder="Choose a package" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {HELICOPTER_PACKAGES.map((pkg) => (
                <SelectItem key={pkg.id} value={pkg.id}>
                  {pkg.name} - {pkg.duration}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Guests Select */}
        <div>
          <label className="block text-sm font-medium mb-2">Guests</label>
          <Select 
            value={guests.toString()} 
            onValueChange={(v) => setGuests(parseInt(v))}
            disabled={selectedPackage?.isPrivateOnly}
          >
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder="Number of guests" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? 'Guest' : 'Guests'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPackage?.isPrivateOnly && (
            <p className="text-xs text-muted-foreground mt-1">Private flight only - up to 5 guests included</p>
          )}
        </div>

        {/* Date Picker */}
        <div>
          <label className="block text-sm font-medium mb-2">Travel Date</label>
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal bg-background",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                disabled={(date) => date < new Date()}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Price Summary */}
        {selectedPackage && (
          <div className="bg-secondary/20 rounded-lg p-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total Amount</span>
              <span className="text-2xl font-bold text-primary">AED {totalPrice.toLocaleString()}</span>
            </div>
            {!selectedPackage.isPrivateOnly && (
              <p className="text-xs text-muted-foreground mt-1">
                AED {selectedPackage.price.toLocaleString()} × {guests} guest{guests > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {/* Book Now Button */}
        <Button 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6"
          onClick={handleBookNow}
          disabled={isProcessing || !packageId || !guests || !date}
        >
          {isProcessing ? "Processing..." : "Book Now"}
        </Button>
      </div>
    </div>
  );
};

export default HelicopterBookingForm;
